import cx from 'classnames'
import { useState } from 'react'

import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './GrowthSettingsModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { RadioGroup } from 'components/RadioGroup'
import { ChipRadioButton } from 'components/RadioGroup/ChipRadioButton'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import {
  BrandPlan,
  BrandPlanContentStrategies,
  ContentFrequencyGrowth,
  ContentStrategy,
  ContentStrategyType,
  StrategyGrowth,
} from 'types'
import { useCallback } from 'use-memo-one'
import dayjs, { Dayjs } from 'dayjs'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useContentDefaults } from 'hooks/queries/useContentDefaults'
import {
  strategiesDefaultData,
  mergeStrategyDefaults,
  buildRemainingContentStrategies,
} from 'screens/VirtualMarketerOnboarding/StrategiesSelection/utils'
import { useContentPlanSchedules } from '../useContentPlanSchedules'
import { EmojiMap } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { ContentStrategyTypeLabel } from '../../../constants'
import { ImgMap } from 'components/ContentStrategyTypeImg/ContentStrategyTypeImg'

const STRATEGY_GROWTH_INTERVAL_WEEKS: Record<StrategyGrowth, number> = {
  manual: 0,
  aggressive: 2,
  moderate: 3,
  conservative: 4,
}

const nextWeekAlignedDate = (afterDate: Dayjs, planStartDate: Dayjs): Dayjs => {
  const planDow = planStartDate.day()
  const afterDow = afterDate.day()
  let daysAhead = (planDow - afterDow + 7) % 7
  if (daysAhead === 0) daysAhead = 7
  return afterDate.add(daysAhead, 'day')
}

interface StrategyDateChange {
  type: ContentStrategyType
  oldDate: string | null
  newDate: string | null
}

interface ActiveStrategy {
  type: ContentStrategyType
  startDate: string
}

interface ScheduleChanges {
  activeStrategies: ActiveStrategy[]
  changes: StrategyDateChange[]
}

const computeScheduleChanges = (
  brandPlan: BrandPlan,
  strategyGrowth: StrategyGrowth,
  newContentStrategies?: BrandPlanContentStrategies,
): ScheduleChanges => {
  const existingStrategies = brandPlan.content_strategies ?? {}
  const now = dayjs()

  const activeStrategies: ActiveStrategy[] = (
    Object.entries(existingStrategies) as [ContentStrategyType, ContentStrategy][]
  )
    .filter(([_, config]) => !config.start_date || dayjs(config.start_date).isBefore(now))
    .map(([type, config]) => ({
      type,
      startDate: config.start_date ?? brandPlan.start_date,
    }))

  const existingFuture = (Object.entries(existingStrategies) as [ContentStrategyType, ContentStrategy][]).filter(
    ([_, config]) => config.start_date && !dayjs(config.start_date).isBefore(now),
  ) as [ContentStrategyType, { start_date: string }][]

  if (strategyGrowth === 'manual') {
    return {
      activeStrategies,
      changes: existingFuture.map(([type, config]) => ({
        type,
        oldDate: config.start_date,
        newDate: null,
      })),
    }
  }

  const mergedStrategies: BrandPlanContentStrategies = { ...existingStrategies, ...newContentStrategies }
  const futureEntries = (
    (Object.entries(mergedStrategies) as [ContentStrategyType, ContentStrategy][]).filter(
      ([_, config]) => config.start_date && !dayjs(config.start_date).isBefore(now),
    ) as [ContentStrategyType, { start_date: string }][]
  ).sort(([_, a], [__, b]) => dayjs(a.start_date).diff(dayjs(b.start_date)))

  const intervalWeeks = STRATEGY_GROWTH_INTERVAL_WEEKS[strategyGrowth]
  const baseDate = nextWeekAlignedDate(dayjs(), dayjs(brandPlan.start_date))

  return {
    activeStrategies,
    changes: futureEntries.map(([type], index) => ({
      type,
      oldDate: existingStrategies[type]?.start_date ?? null,
      newDate: baseDate.add((index + 1) * intervalWeeks, 'week').format('YYYY-MM-DD'),
    })),
  }
}

const formatDate = (dateStr: string) => dayjs(dateStr).format('MMMM D, YYYY')

const ConfirmModal = ({
  onConfirm,
  onCancel,
  activeStrategies,
  changes,
}: {
  onConfirm: () => void
  onCancel: () => void
  activeStrategies: ActiveStrategy[]
  changes: StrategyDateChange[]
}) => {
  return (
    <Modal.Root size="md">
      <Modal.Header title="Updated Schedule" />
      <Modal.Content>
        <div className={Styles.confirmContent}>
          <div className={Styles.activeStrategies}>
            <Heading level={4}>Active Strategies</Heading>
            {activeStrategies.map(({ type, startDate }, index) => (
              <div key={type} className={cx(Styles.confirmRow, Styles[type])}>
                <div className={Styles.numberContainer}>
                  <div className={cx(Styles.number, Styles.other)}>{index + 1}</div>
                </div>
                <div className={Styles.text}>
                  <Heading level={3} color="var(--light-100)">
                    {EmojiMap[type]} {ContentStrategyTypeLabel[type]}
                  </Heading>
                  <div className={Styles.dateChange}>
                    <Text variant="secondary" color="var(--light-100)">
                      Started on {formatDate(startDate)}
                    </Text>
                  </div>
                </div>
                {ImgMap[type] && <img className={Styles.img} src={ImgMap[type]} alt={ContentStrategyTypeLabel[type]} />}
              </div>
            ))}
          </div>
          <div className={Styles.nextUpStrategies}>
            <Heading level={4}>Next Up:</Heading>
            {changes.map(({ type, oldDate, newDate }, index) => (
              <div key={type} className={cx(Styles.confirmRow, Styles[type])}>
                <div className={Styles.numberContainer}>
                  <div
                    className={cx(Styles.number, {
                      [Styles.nextUp]: index === 0,
                      [Styles.other]: index !== 0,
                    })}
                  >
                    {activeStrategies.length + index + 1}
                  </div>
                </div>
                <div className={Styles.text}>
                  <Heading level={3} color="var(--light-100)">
                    {EmojiMap[type]} {ContentStrategyTypeLabel[type]}
                  </Heading>
                  <div className={Styles.dateChange}>
                    {newDate == null && (
                      <Text variant="secondary" color="var(--light-100)">
                        Will be removed
                      </Text>
                    )}
                    {newDate !== null && oldDate !== null && (
                      <>
                        <Text variant="secondary" color="var(--light-100)">
                          Now launching on {formatDate(newDate)}
                        </Text>
                        <Text variant="secondary" color="var(--light-60)">
                          Instead of <span className={Styles.strikethrough}>{formatDate(oldDate)}</span>
                        </Text>
                      </>
                    )}
                    {newDate !== null && oldDate == null && (
                      <Text variant="secondary" color="var(--light-100)">
                        Launching on {formatDate(newDate)}
                      </Text>
                    )}
                  </div>
                </div>
                {ImgMap[type] && <img className={Styles.img} src={ImgMap[type]} alt={ContentStrategyTypeLabel[type]} />}
              </div>
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="secondary" onPress={onCancel}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterButton onPress={onConfirm}>Confirm Changes</Modal.FooterButton>
      </Modal.Footer>
    </Modal.Root>
  )
}

export const GrowthSettingsModal = ({ brandPlan }: { brandPlan: BrandPlan }) => {
  const { closeModal, openModal, closeAllModals } = useModals()
  const [contentFrequency, setContentFrequency] = useState<ContentFrequencyGrowth>(
    brandPlan.content_optimization_strategy?.frequency_growth?.type || 'flat',
  )
  const [strategyGrowth, setStrategyGrowth] = useState<StrategyGrowth>(
    brandPlan.content_optimization_strategy?.strategy_growth?.type || 'manual',
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const { mutateAsync } = useUpdateBrandPlan({ id: brandPlan?.id })
  const { refetch: refetchBrandPlan } = useCurrentBrandPlan()
  const { refetch: refetchSchedules } = useContentPlanSchedules({ startDate: dayjs().format('YYYY-MM-DD') })
  const { data: contentDefaults } = useContentDefaults()

  const previousGrowthType = brandPlan.content_optimization_strategy?.strategy_growth?.type
  const hasNoContentStrategies = !brandPlan.content_strategies || Object.keys(brandPlan.content_strategies).length === 0
  const needsContentStrategies =
    (!previousGrowthType || previousGrowthType === 'manual' || hasNoContentStrategies) && strategyGrowth !== 'manual'

  const preparePayload = useCallback((): { content_strategies?: BrandPlanContentStrategies } => {
    let contentStrategiesPayload: { content_strategies?: BrandPlanContentStrategies } = {}
    if (needsContentStrategies) {
      const businessType = brandPlan.business_type ?? 'service'
      const mergedDefaults = mergeStrategyDefaults(strategiesDefaultData, contentDefaults?.content_strategy_defaults)
      const activeStrategyKeys = new Set(Object.keys(brandPlan.content_strategies ?? {}) as ContentStrategyType[])
      const allStrategies = mergedDefaults[businessType].strategies.map((s) => ({
        ...s,
        isSelected: activeStrategyKeys.has(s.id),
      }))
      let baseStartDate = undefined
      switch (strategyGrowth) {
        case 'conservative':
          baseStartDate = dayjs().add(4, 'weeks')
          break
        case 'moderate':
          baseStartDate = dayjs().add(3, 'weeks')
          break
        case 'aggressive':
          baseStartDate = dayjs().add(2, 'weeks')
          break
      }
      const remainingStrategies = buildRemainingContentStrategies(allStrategies, strategyGrowth, baseStartDate)
      contentStrategiesPayload = {
        content_strategies: {
          ...brandPlan.content_strategies,
          ...remainingStrategies,
        },
      }
    }

    return contentStrategiesPayload
  }, [brandPlan, strategyGrowth, needsContentStrategies, contentDefaults?.content_strategy_defaults])

  const openConfirmModal = () => {
    const contentStrategiesPayload = preparePayload()
    const { activeStrategies, changes } = computeScheduleChanges(
      brandPlan,
      strategyGrowth,
      contentStrategiesPayload.content_strategies,
    )
    if (changes.length === 0) {
      updateBrandPlan({ toastMessage: 'Post count increase settings saved!' })
      return
    }
    openModal(ConfirmModal, {
      onConfirm: () => updateBrandPlan({ toastMessage: 'Strategy dates updated based on your new growth settings!' }),
      onCancel: closeModal,
      activeStrategies,
      changes,
    })
  }

  const updateBrandPlan = useCallback(
    async ({ toastMessage }: { toastMessage: string }) => {
      setIsProcessing(true)

      const contentStrategiesPayload = preparePayload()
      await mutateAsync({
        content_optimization_strategy: {
          strategy_growth: { type: strategyGrowth, start_date: dayjs().format('YYYY-MM-DD') },
          frequency_growth: { type: contentFrequency, start_date: dayjs().format('YYYY-MM-DD') },
        },
        ...contentStrategiesPayload,
      })
      await refetchBrandPlan()
      await refetchSchedules()
      setIsProcessing(false)
      closeAllModals()
      showToastMessage(toastMessage)
    },
    [closeAllModals, mutateAsync, refetchBrandPlan, refetchSchedules, strategyGrowth, contentFrequency, preparePayload],
  )

  const strategyGrowthChanged = brandPlan?.content_optimization_strategy?.strategy_growth?.type !== strategyGrowth
  const frequencyGrowthChanged = brandPlan?.content_optimization_strategy?.frequency_growth?.type !== contentFrequency
  const noChanges = !strategyGrowthChanged && !frequencyGrowthChanged

  return (
    <Modal.Root bodyClassName={Styles.modalRoot}>
      <Modal.Header title="Change Growth Settings" />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.leftColumn}>
            <div className={Styles.header}>
              <Heading level={3}>How fast do you want Blaze to introduce new types of content?</Heading>
            </div>
            <div className={Styles.body}>
              <RadioGroup
                value={strategyGrowth}
                onChange={(value) => {
                  value && setStrategyGrowth(value as StrategyGrowth)
                }}
                className={Styles.radioGroup}
              >
                <ChipRadioButton value="aggressive" className={Styles.chip} deletable={false} variant="flat" size="xl">
                  <div className={Styles.chipHeader}>
                    <Heading level={4}>Aggressive</Heading>
                    <Text color="var(--dark-60)">+1 strategy every 2 weeks </Text>
                  </div>
                  <div className={Styles.chipFooter}>
                    <Text variant="secondary" color="var(--dark-60)">
                      ~300% growth over 6 months
                    </Text>
                  </div>
                </ChipRadioButton>
                <ChipRadioButton value="moderate" className={Styles.chip} deletable={false} variant="flat" size="xl">
                  <div className={Styles.chipHeader}>
                    <Heading level={4}>Moderate</Heading>
                    <Text color="var(--dark-60)">+1 strategy every 3 weeks </Text>
                  </div>
                  <div className={Styles.chipFooter}>
                    <Text variant="secondary" color="var(--dark-60)">
                      ~200% growth over 6 months
                    </Text>
                  </div>
                </ChipRadioButton>
                <ChipRadioButton
                  value="conservative"
                  className={Styles.chip}
                  deletable={false}
                  variant="flat"
                  size="xl"
                >
                  <div className={Styles.chipHeader}>
                    <Heading level={4}>Conservative</Heading>
                    <Text color="var(--dark-60)">+1 strategy every 4 weeks </Text>
                  </div>
                  <div className={Styles.chipFooter}>
                    <Text variant="secondary" color="var(--dark-60)">
                      ~100% growth over 6 months
                    </Text>
                  </div>
                </ChipRadioButton>
                <ChipRadioButton value="manual" className={Styles.chip} deletable={false} variant="flat" size="xl">
                  <div className={Styles.chipHeader}>
                    <Heading level={3}>Manual</Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      I'll choose when to add a new strategy
                    </Text>
                  </div>
                </ChipRadioButton>
              </RadioGroup>
            </div>
          </div>
          <div className={Styles.rightColumn}>
            <div className={Styles.header}>
              <Heading level={3}>How fast do you want Blaze to grow content within a strategy?</Heading>
            </div>
            <div className={Styles.body}>
              <RadioGroup
                value={contentFrequency}
                onChange={(value) => {
                  value && setContentFrequency?.(value as ContentFrequencyGrowth)
                }}
                className={Styles.radioGroup}
              >
                <ChipRadioButton value="competitive" className={Styles.chip} deletable={false} variant="flat" size="xl">
                  <div className={Styles.chipHeader}>
                    <Heading level={3}>Competitive</Heading>
                    <Text>+2 additional weekly posts per month</Text>
                  </div>
                  <div className={Styles.chipFooter}>
                    <Credits size={16} color="var(--dark-60)" />
                    <Text variant="secondary" color="var(--dark-60)">
                      40 additional credits per month{' '}
                    </Text>
                  </div>
                </ChipRadioButton>
                <ChipRadioButton value="smart" className={Styles.chip} deletable={false} variant="flat" size="xl">
                  <div className={Styles.chipHeader}>
                    <Heading level={3}>Smart</Heading>
                    <Text>+1 additional weekly post per month</Text>
                  </div>
                  <div className={Styles.chipFooter}>
                    <Credits size={16} color="var(--dark-60)" />
                    <Text variant="secondary" color="var(--dark-60)">
                      20 additional credits per month{' '}
                    </Text>
                  </div>
                </ChipRadioButton>
                <ChipRadioButton value="flat" className={Styles.chip} deletable={false} variant="flat" size="xl">
                  <div className={Styles.chipHeader}>
                    <Heading level={3}>Flat</Heading>
                    <Text>0 additional weekly posts</Text>
                    <Text variant="secondary">I'll manually manage my frequencies</Text>
                  </div>
                </ChipRadioButton>
              </RadioGroup>
              <Text variant="metadata" color="var(--dark-60)">
                We will publish no more than 2 posts per day, with a maximum of 15 posts per week.
              </Text>
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} variant="secondary">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterButton
          onPress={
            strategyGrowthChanged
              ? openConfirmModal
              : () => updateBrandPlan({ toastMessage: 'Post count increase settings saved!' })
          }
          isDisabled={noChanges || isProcessing}
        >
          {strategyGrowthChanged ? 'Confirm' : 'Save Changes'}
        </Modal.FooterButton>
      </Modal.Footer>
    </Modal.Root>
  )
}
